import React, { Component } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './ProjectStatusBox.scss';
import ActionDropdown from '../common/ActionDropdown/ActionDropdown';
import MarkOnHold from '../MarkOnHold';
import EditInvoiceRate from '../EditInvoiceRate';
import ExecuteAgreementForProject from '../ExecuteAgreementForProject/ExecuteAgreementForProject';
import FullTimeRatesModal from '../FullTimeRatesModal/FullTimeRatesModal';
import WorkCompletedForProject from '../WorkCompletedForProject/WorkCompletedForProject';
import ProjectClosure from '../ProjectClosure/ProjectClosure';

const repProjectStatusMap = new Map([
  ['employer_requested_matching', 'Employer Requested Matching'],
  ['clora_matching', 'Project In Matching'],
  ['clora_matched', 'Project Matched'],
  ['executed_agreement', 'Project Executed Agreement'],
  ['work_started', 'Project Work Started'],
  ['work_completed', 'Project Work Completed'],
  ['closed', 'Project Closed'],
  ['on_hold', 'Project On Hold'],
  ['employer_draft', 'Employer Draft'],
]);

const renderEmployerProjectStatus = (status, isOpenMarketplaceOrRehire) => {
  if (status === 'employer_draft') {
    return (
      <h2 className="demibold">Draft</h2>
    );
  } if (status === 'employer_requested_matching') {
    if (isOpenMarketplaceOrRehire) {
      return (
        <h2 className="demibold">Live!</h2>
      );
    }
    return (
      <h2 className="demibold">
        Under Review&nbsp;
        <OverlayTrigger
          placement="right"
          delay={100}
          overlay={(
            <Tooltip id="project-status-tooltip">
              Clora will help you to shape your project&#39;s needs in a scoping call!
            </Tooltip>
          )}
        >
          <i className="fas fa-info-circle" />
        </OverlayTrigger>
      </h2>
    );
  } if (status === 'clora_matching') {
    return (
      <h2 className="demibold">
        Sourcing&nbsp;
        <OverlayTrigger
          placement="right"
          delay={100}
          overlay={(
            <Tooltip id="project-status-tooltip">
              Your project is now public in our marketplace and our life-science
              experts are vetting candidates for you.
            </Tooltip>
          )}
        >
          <i className="fas fa-info-circle" />
        </OverlayTrigger>
      </h2>
    );
  } if (status === 'closed') {
    return (
      <h2 className="demibold">Closed</h2>
    );
  } if (status === 'clora_matched' || status === 'executed_agreement') {
    return (
      <h2 className="demibold">Matched</h2>
    );
  } if (status === 'work_started') {
    return (
      <h2 className="demibold">Work Started</h2>
    );
  } if (status === 'work_completed') {
    return (
      <h2 className="demibold">Work Completed</h2>
    );
  } if (status === 'on_hold') {
    return (
      <h2 className="demibold">On Hold</h2>
    );
  }
  return null;
};

const handleDeleteProject = (redirectUrl) => {
  window.location.href = redirectUrl.url;
};

class ProjectStatusBox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPublic: props.projectIsPublic,
      isFulfillmentPaused: props.projectHasFulfillmentPaused,
      status: props.projectStatus,
    };
    this.getLinks = this.getLinks.bind(this);
    this.handlePublicToggle = this.handlePublicToggle.bind(this);
    this.handleFulfillmentToggle = this.handleFulfillmentToggle.bind(this);
    this.handleProjectStatusUpdate = this.handleProjectStatusUpdate.bind(this);
  }

  getLinks() {
    const {
      canBeginMatching, beginMatchingLink, csrfToken, resourceId, currentUserIsAdmin,
      executeAgreementUrl, projectTakeRate, markWorkStartedLink, completeWorkUrl,
      projectCanBeOnHold, markOnHoldLink, resumeProjectFulfillment, pauseProjectFulfillment,
      projectIsClosed, closeProjectUrl, primaryCloseReason, secondaryCloseReason,
      projectIsMatching, makeProjectPrivate, makeProjectPublic,
      projectChangeHistory, projectStatusChange, projectCanBeDeleted, projectPath,
      cloraTakeRate, acceptedProposal, employmentType, quickbooksPaymentTerm,
      quickbooksPaymentTermOptions,
    } = this.props;
    const { isPublic, isFulfillmentPaused, status } = this.state;
    const links = [];

    if (canBeginMatching && status !== 'clora_matching') {
      links.push(['put',
        <span>
          Update Status to:&nbsp;
          <strong>Clora Matching</strong>
        </span>,
        beginMatchingLink,
        this.handleProjectStatusUpdate]);
    }

    if (status === 'clora_matched') {
      if (employmentType === 'full_time') {
        links.push(['component',
          <FullTimeRatesModal
            mode="execute"
            triggerStyle="dropdown-item"
            triggerText={(
              <span>
                Update Status to:&nbsp;
                <strong>Executed Agreement</strong>
              </span>
)}
            csrfToken={csrfToken}
            putUrl={executeAgreementUrl}
            projectTakeRate={projectTakeRate}
            quickbooksPaymentTerm={quickbooksPaymentTerm}
            quickbooksPaymentTermOptions={quickbooksPaymentTermOptions}
          />]);
      } else {
        links.push(['component',
          <ExecuteAgreementForProject
            triggerStyle="dropdown-item"
            triggerText={(
              <span>
                Update Status to:&nbsp;
                <strong>Executed Agreement</strong>
              </span>
            )}
            csrfToken={csrfToken}
            executeAgreementUrl={executeAgreementUrl}
            projectTakeRate={projectTakeRate}
            quickbooksPaymentTerm={quickbooksPaymentTerm}
            quickbooksPaymentTermOptions={quickbooksPaymentTermOptions}
          />]);
      }
    }

    if (status === 'executed_agreement') {
      links.push(['put',
        <span>
          Update Status to:&nbsp;
          <strong>Work Started</strong>
        </span>,
        markWorkStartedLink,
        this.handleProjectStatusUpdate]);
    }

    if (status === 'work_started') {
      links.push(['component',
        <WorkCompletedForProject
          triggerStyle="dropdown-item"
          triggerText={(
            <span>
              Update Status to:&nbsp;
              <strong>Work Completed</strong>
            </span>
)}
          csrfToken={csrfToken}
          completeWorkUrl={completeWorkUrl}
        />]);
    }

    if (projectCanBeOnHold) {
      links.push(['component',
        <MarkOnHold
          triggerStyle="dropdown-item"
          triggerText={(
            <span>
              Update Status to:&nbsp;
              <strong>On Hold</strong>
            </span>
)}
          triggerType="link"
          formUrl={markOnHoldLink}
          csrfToken={csrfToken}
        />]);
    }

    if (projectIsClosed) {
      links.push(['component',
        <ProjectClosure
          csrfToken={csrfToken}
          triggerStyle="dropdown-item"
          triggerText="Update Closed Reason"
          closeProjectUrl={closeProjectUrl}
          resourceId={resourceId}
          primaryReason={primaryCloseReason}
          secondaryReason={secondaryCloseReason}
        />]);
    } else {
      links.push(['component',
        <ProjectClosure
          triggerStyle="dropdown-item"
          triggerText={(
            <span>
              Update Status to:&nbsp;
              <strong>Closed</strong>
            </span>
)}
          csrfToken={csrfToken}
          closeProjectUrl={closeProjectUrl}
          resourceId={resourceId}
        />]);
    }

    links.push(['separator', '----', () => {}, null, 'separator1']);

    if (projectIsMatching) {
      if (isPublic) {
        links.push(['put', 'Remove from Open Opp.', makeProjectPrivate, this.handlePublicToggle]);
      } else {
        links.push(['put', 'Show project on Open Opp.', makeProjectPublic, this.handlePublicToggle]);
      }

      if (isFulfillmentPaused) {
        links.push(['put', 'Resume fulfillment', resumeProjectFulfillment, this.handleFulfillmentToggle]);
      } else {
        links.push(['put', 'Pause fulfillment', pauseProjectFulfillment, this.handleFulfillmentToggle]);
      }

      links.push(['separator', '----', () => {}, null, 'separator2']);
    }

    if (['executed_agreement', 'work_started', 'work_completed'].includes(status) && acceptedProposal) {
      links.push(['component',
        (employmentType === 'full_time'
          ? (
            <FullTimeRatesModal
              mode="edit"
              triggerStyle="dropdown-item"
              triggerText="Edit Rates"
              submitButtonText="Yes, Update Rates"
              csrfToken={csrfToken}
              putUrl={`/projects/${resourceId}`}
              baseAnnualPay={acceptedProposal.base_annual_pay}
              conversionFee={acceptedProposal.conversion_fee}
              projectTakeRate={projectTakeRate}
              quickbooksPaymentTerm={quickbooksPaymentTerm}
              quickbooksPaymentTermOptions={quickbooksPaymentTermOptions}
            />
          )
          : (
            <EditInvoiceRate
              net_rate={acceptedProposal.net_rate / 100.0}
              travel_rate={acceptedProposal.travel_rate / 100.0}
              gross_rate={acceptedProposal.gross_rate / 100.0}
              gross_travel_rate={acceptedProposal.gross_travel_rate / 100.0}
              hourLimit={acceptedProposal.hour_limit}
              takeRate={cloraTakeRate * 100}
              csrfToken={csrfToken}
              putUrl={`/projects/${resourceId}`}
              isEditingProposal={false}
              className="edit-invoice-rate mr-5"
              quickbooksPaymentTerm={quickbooksPaymentTerm}
              quickbooksPaymentTermOptions={quickbooksPaymentTermOptions}
            />
          )
        ),
      ]);
    }

    links.push(['link', 'See change history', projectChangeHistory]);

    if (currentUserIsAdmin) {
      links.push(['link', 'Force update status', projectStatusChange]);
    }

    if (projectCanBeDeleted) {
      links.push(['delete', 'Delete This Project', projectPath, handleDeleteProject]);
    }

    return links;
  }

  handlePublicToggle(publicStatus) {
    this.setState({
      isPublic: publicStatus,
    });
  }

  handleFulfillmentToggle(fulfillmentStatus) {
    this.setState({
      isFulfillmentPaused: fulfillmentStatus,
    });
  }

  handleProjectStatusUpdate(project) {
    this.setState({
      status: project.status,
    });
  }

  render() {
    const { csrfToken, currentUserIsRep, isOpenMarketplaceOrRehire } = this.props;
    const { status } = this.state;

    return (
      <div className="project-status-container">
        <div className="project-status-container-item">Project Status:</div>
        {currentUserIsRep ? (
          <ActionDropdown
            id="rep-actions"
            toggleStyle="project-status-container-item"
            defaultLabel={repProjectStatusMap.get(status)}
            csrfToken={csrfToken}
            links={this.getLinks()}
            buttonVariant="default"
          />
        ) : (
          <div className="project-status-container-item">
            {renderEmployerProjectStatus(status, isOpenMarketplaceOrRehire)}
          </div>
        )}
      </div>
    );
  }
}

ProjectStatusBox.propTypes = {
  currentUserIsRep: PropTypes.bool.isRequired,
  currentUserIsAdmin: PropTypes.bool.isRequired,
  projectStatus: PropTypes.string.isRequired,
  resourceId: PropTypes.string.isRequired,
  projectPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  isOpenMarketplaceOrRehire: PropTypes.bool,
  canBeginMatching: PropTypes.bool.isRequired,
  beginMatchingLink: PropTypes.string.isRequired,
  projectIsMatching: PropTypes.bool.isRequired,
  executeAgreementUrl: PropTypes.string.isRequired,
  projectTakeRate: PropTypes.string.isRequired,
  markWorkStartedLink: PropTypes.string.isRequired,
  completeWorkUrl: PropTypes.string.isRequired,
  projectCanBeOnHold: PropTypes.bool.isRequired,
  markOnHoldLink: PropTypes.string.isRequired,
  projectIsClosed: PropTypes.bool.isRequired,
  closeProjectUrl: PropTypes.string.isRequired,
  primaryCloseReason: PropTypes.string,
  secondaryCloseReason: PropTypes.string,
  projectIsPublic: PropTypes.bool.isRequired,
  makeProjectPrivate: PropTypes.string.isRequired,
  makeProjectPublic: PropTypes.string.isRequired,
  projectHasFulfillmentPaused: PropTypes.bool.isRequired,
  resumeProjectFulfillment: PropTypes.string.isRequired,
  pauseProjectFulfillment: PropTypes.string.isRequired,
  projectChangeHistory: PropTypes.string.isRequired,
  projectStatusChange: PropTypes.string.isRequired,
  projectCanBeDeleted: PropTypes.bool.isRequired,
  cloraTakeRate: PropTypes.string.isRequired,
  acceptedProposal: PropTypes.shape({}),
  employmentType: PropTypes.string.isRequired,
  quickbooksPaymentTerm: PropTypes.string.isRequired,
  quickbooksPaymentTermOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

ProjectStatusBox.defaultProps = {
  isOpenMarketplaceOrRehire: false,
  primaryCloseReason: '',
  secondaryCloseReason: '',
  acceptedProposal: null,
  quickbooksPaymentTermOptions: [],
};

export default ProjectStatusBox;
